import { FC, useEffect } from 'react';
import { navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import {
  useMeQuery,
  useUpdateSubscriptionMutation,
  useCreateSubscriptionMutation,
  useSubscriptionProductsQuery,
  useCheckPostalCodeMutation,
  useUpdateUserMutation
} from 'lib/home/generated/queries';
import { Grid } from 'lib/components';
import { RegistrationRouteMap, RegistrationStep, useRegistrationStore } from '../../store';
import {
  GettingStartedContent,
  GettingStartedLayout,
  GettingStartedPauseSkipAnytimeFooter,
  SelectPlanCard,
  SelectPlanCardContent,
  SelectPlanForm,
  SelectPlanFormValues
} from '../../components';
import { getQueryParams } from 'lib/util';

const SelectPlanPage: FC = () => {
  // TODO: Update this to use our useSubscriptionProductsStaticQuery when the data is sourced into Gatsby()
  const { mutate: updateSubscriptionMutation } = useUpdateSubscriptionMutation();
  const { mutate: createSubscriptionMutation } = useCreateSubscriptionMutation();
  const { promoCode, referralCode, selectedPlan, setSelectedPlan, setSelectedPlanName } = useRegistrationStore();
  const { data: subscriptionProducts } = useSubscriptionProductsQuery();
  const { mutate: checkPostalCode } = useCheckPostalCodeMutation();
  const { mutate: updateUser } = useUpdateUserMutation();
  const { data: user, isError } = useMeQuery();
  const isAuthorized = !isError && !!user;

  useEffect(() => {
    const queryParams = getQueryParams();
    const params = {
      postal_code: queryParams.get('postalCode'),
      first_name: queryParams.get('firstName'),
      last_name: queryParams.get('lastName')
    };

    if (params.postal_code && params.first_name && params.last_name) {
      checkPostalCode(
        { code: params.postal_code },
        {
          onSuccess: result => {
            const { postalCodeAllowed } = result;
            if (!postalCodeAllowed)
              return navigate(RegistrationRouteMap.unsupportedPostalCode, {
                state: { postal_code: params.postal_code }
              });

            if (isAuthorized)
              updateUser({
                userInput: {
                  postal_code: params.first_name,
                  first_name: params.first_name,
                  last_name: params.last_name,
                  id: user.me.id
                }
              });
          }
        }
      );
    }
  }, []);

  const handleSubmit = (values: SelectPlanFormValues) => {
    window.dataLayer.push({
      event: 'select_plan',
      selected_plan: values.selectedPlan
    });

    if (!isAuthorized || !user.me.meal_preferences) {
      const selectedPlan = subscriptionProducts.standardSubscriptions.find(s => s.id === values.selectedPlan);

      setSelectedPlan(values.selectedPlan);
      setSelectedPlanName(selectedPlan?.title);
      navigate(RegistrationRouteMap.preferences);
      return;
    }

    if (!user?.me?.subscription) {
      return createSubscriptionMutation(
        {
          createUserSubscriptionInput: {
            product_variant_id: values.selectedPlan,
            promo_code: promoCode,
            referral_code: referralCode
          }
        },
        {
          onSuccess: ({ createSubscription }) => {
            setSelectedPlan(createSubscription.product_variant_id);
            navigate(RegistrationRouteMap.preferences);
          }
        }
      );
    }

    updateSubscriptionMutation(
      { updateUserSubscriptionInput: { product_variant_id: values.selectedPlan } },
      {
        onSuccess: ({ updateSubscription }) => {
          setSelectedPlan(updateSubscription.product_variant_id);
          navigate(RegistrationRouteMap.preferences);
        }
      }
    );
  };

  return (
    <GettingStartedLayout className="getting-started-select-plan" step={RegistrationStep.selectPlan}>
      <GettingStartedContent>
        <Grid>
          <div className="col-span-12 lg:col-span-10 lg:col-start-2">
            <SelectPlanCard>
              <SelectPlanCardContent>
                <h2>Select Your Plan</h2>
                {promoCode && (
                  <p>
                    Your promo code <b>{promoCode}</b> will be applied at checkout
                  </p>
                )}
                <SelectPlanForm initialValues={{ selectedPlan: selectedPlan ?? '' }} handleSubmit={handleSubmit} />
              </SelectPlanCardContent>
            </SelectPlanCard>
          </div>
        </Grid>
      </GettingStartedContent>

      <div className="bg-primary h-20 relative lg:hidden">
        <StaticImage
          className="z-10 !absolute left-1/2 bottom-0 -translate-x-1/2 max-w-[180px]"
          src="../../images/chicken-skewer-dish.png"
          alt="A delicious plate of carrots, cucumbers, jalapeños, and shrimp."
          placeholder="blurred"
        />
      </div>
      <GettingStartedPauseSkipAnytimeFooter className="!mt-0" />
    </GettingStartedLayout>
  );
};

export default SelectPlanPage;
